import React from 'react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';

import { logEvent } from '@hopdrive/sdk-events';
import { useRooftopProductFacts } from '@hopdrive/feature-portal-products';
import {
  resolveCapabilities,
  type MovePlannerHostValue,
  type PlannerLane,
  type PlannerLanesPort,
  type PlannerNotifyOptions,
  type PlannerProgramsPort,
  type PlannerRooftop,
  type PlannerSession,
  type PlannerTransport,
} from '@hopdrive/feature-move-planner';

import siteConfig from '@config';
import { useUser, useRooftop, setRooftop, useCustomerConfig, setCustomerConfig, setOrganizationConfig } from '@store';
import { checkForPowerRole, getClaimsFromToken, getTokenInfo } from '@features/auth/services/authService';
import { useFeaturePermission } from '@utils/featurePermissions';
import { enrichCustomerConfig, enrichOrganizationConfig } from '@utils/configsService';
import { decodeVin } from '@utils/decodeVin/decodeVin';
import { useAddressSuggestions } from '@hooks/useAddressSuggestions';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/ModalComponents';
import { useDisabledBypassTrap } from '@components/DisabledBypassTrap';
import { useBusinessHoursForTimeline } from '@features/schedule/hooks/useBusinessHoursForTimeline';
import { useDealerDayScenarios, useDealerLiteDrivers } from './plannerScheduleReads';
import { dispatchLiteMove, refetchMoveWithRelationships } from '@features/moves/services/moveDispatchService';
import { useGetMoveForDispatchLazyQuery } from '@features/moves/queries/getMoveForDispatch.query.generated';
import { useUpdateMoveTimesMutation } from '@features/moves/queries/updateMoveTimes.mutation.generated';
import { useGetLaneByCustomerAndLocationsLazyQuery } from '@features/schedule/queries/getLaneByCustomerAndLocations.query.generated';
import { lanesReadyForRequest } from '@hopdrive/feature-lane-lifecycle';
import { useLaneEnrichment } from '@features/moveCreation/hooks/useLaneEnrichment';
import { laneNeedsEnrichment, mergeLaneEstimates } from '@features/moveCreation/utils/laneEnrichment';
import { useProgramCoverageQuote } from '@features/moveCreation/hooks/useProgramCoverageQuote';
import MovePlannerProgramEligibility from '@features/moveCreation/components/MovePlannerProgramEligibility';
import type { Lanes, Moves } from '@gql/base-types';

const ROOFTOP_KEY = 'rooftopId';

const lanes: PlannerLanesPort = {
  useLaneEnrichment: (lane, generation, onUpdate) =>
    useLaneEnrichment(lane as Lanes | null | undefined, generation, onUpdate as (updated: Lanes) => void),
  laneNeedsEnrichment: lane => laneNeedsEnrichment(lane as Lanes | null | undefined),
  mergeLaneEstimates: (existing, enriched) =>
    mergeLaneEstimates(existing as unknown as Lanes, enriched as unknown as Lanes) as unknown as typeof existing,
  lanesReadyForRequest: (mainLane, inverseLane, requiresInverseLane) =>
    lanesReadyForRequest(mainLane as never, inverseLane as never, requiresInverseLane),
};

const programs: PlannerProgramsPort = {
  useCoverageQuote: () => {
    const coverage = useProgramCoverageQuote();
    return { ...coverage, priceReady: coverage.vehicleReady };
  },
  EligibilitySummary: MovePlannerProgramEligibility,
};

/** `logEvent` answers with a result when the payload asks for one (`safeWithResponse`). */
const awaitableLogEvent = logEvent as unknown as (key: string, options: Record<string, unknown>) => Promise<unknown>;

const notify = (kind: 'success' | 'error' | 'warning' | 'info') => (message: string, options: PlannerNotifyOptions) =>
  toast[kind](message, options);

function restoreRooftopId(): number | undefined {
  const stored = localStorage?.getItem(ROOFTOP_KEY);
  if (!stored) return undefined;
  const id = parseInt(stored, 10);
  return Number.isFinite(id) ? id : undefined;
}

/** The Dealer's answers to everything the Move Planner needs from its host. */
export function useDealerPlannerHost(): MovePlannerHostValue {
  const user = useUser();
  const [getLane] = useGetLaneByCustomerAndLocationsLazyQuery();
  const [getMoveForDispatch] = useGetMoveForDispatchLazyQuery();
  const [updateMoveTimes] = useUpdateMoveTimesMutation();

  const transport = React.useMemo<PlannerTransport>(
    () => ({
      checkBusinessHours: async ({ lane, readyBy, isMovePair }) => {
        const userToken = await user?.getToken();
        const response = await fetch('/.netlify/functions/checkBusinessHours', {
          method: 'POST',
          body: JSON.stringify({ lane, ready_by: readyBy, isMovePair }),
          headers: { authorization: `Bearer ${userToken}` },
        });
        return response.json();
      },
      buildLaneAndInverse: async ({ customerId, pickup, delivery }) => {
        const userToken = await user?.getToken();
        const res = await axios.post(
          '/.netlify/functions/buildLaneAndInverse',
          { lane: { customerId, pickup, delivery } },
          { headers: { authorization: `Bearer ${userToken}` } }
        );
        return res?.data?.success ? (res.data.upsertableLanes as PlannerLane[]) : null;
      },
      findLanePair: async ({ customerId, pickupId, deliveryId }) => {
        const [forward, inverse] = await Promise.all([
          getLane({ variables: { customerId, pickupId, deliveryId } }),
          getLane({ variables: { customerId, pickupId: deliveryId, deliveryId: pickupId } }),
        ]);
        return {
          forward: (forward.data?.lanes?.[0] as PlannerLane | undefined) ?? null,
          inverse: (inverse.data?.lanes?.[0] as PlannerLane | undefined) ?? null,
        };
      },
      decodeVin: vin => decodeVin(vin, true) as Promise<Record<string, unknown>>,
      verifyMobilePhone: async phone => {
        const userToken = await user?.getToken();
        const res = await axios({
          method: `POST`,
          url: `/.netlify/functions/verifyMobilePhone`,
          data: { phone },
          headers: { authorization: `Bearer ${userToken}` },
        });
        return res?.data?.success && (res?.data?.isMobile === true || res?.data?.isMobile === false) ? res.data.isMobile : null;
      },
      writeAdminNotification: async variables => {
        const userToken = await user?.getToken();
        await axios({
          method: `POST`,
          url: `/.netlify/functions/writeAdminNotification`,
          data: { variables },
          headers: { authorization: `Bearer ${userToken}` },
        });
      },
      useAddressSuggestions: () => useAddressSuggestions(),
      useDayScenarios: input => useDealerDayScenarios(input),
      useBusinessHours: (locationId, date, timezone) => useBusinessHoursForTimeline(locationId, date, timezone),
      useLiteDrivers: (customerId, isLite) => useDealerLiteDrivers(customerId, isLite),
      dispatch: {
        refetchMove: async moveId => (await refetchMoveWithRelationships(moveId, getMoveForDispatch)) as { id: number } | null,
        dispatchToDriver: async ({ move, driverId, time, withRideshare }) => {
          await dispatchLiteMove(move as unknown as Moves, driverId, time, 'assign', user || null, updateMoveTimes, window.location.origin, withRideshare);
        },
      },
    }),
    [getLane, getMoveForDispatch, updateMoveTimes, user]
  );

  // The session is its own value, stable while the user is, so the planner's per-session loads do not rerun
  // when another part of the host changes.
  const session = React.useMemo<PlannerSession>(
    () => ({
      customerId: user?.profile?.customer_id ?? undefined,
      actorEmail: user?.profile?.email || null,
      userId: user?.profile?.id ?? null,
      allowedPayerIds: user?.claims?.['x-hasura-allowed-payers'] || [],
      isPowerRole: checkForPowerRole(user),
      readClaims: async () => {
        if (!user) return null;
        const userToken = await user.getToken();
        if (!userToken) throw new Error(`User token not found`);
        const claims = await getClaimsFromToken();
        if (!claims) throw new Error(`User claims not found`);
        return { role: claims['x-hasura-default-role'] };
      },
      getToken: async () => (await user?.getToken()) ?? undefined,
      tokenInfo: getTokenInfo,
      reportIdentity: {
        role: user?.claims?.['x-hasura-default-role'],
        customerId: user?.claims?.['x-hasura-customer-id'],
      },
      useFeature: (name, rooftopId) => useFeaturePermission(name as Parameters<typeof useFeaturePermission>[0], rooftopId),
    
    }),
    [user]
  );

  return React.useMemo<MovePlannerHostValue>(
    () => ({
      session,
      rooftop: {
        useWorkingRooftop: () => useRooftop() as PlannerRooftop | null | undefined,
        setWorkingRooftop: rooftop => setRooftop(rooftop as never),
        useCustomerConfig: () => useCustomerConfig(),
        setCustomerConfig: config => setCustomerConfig(config),
        changeWorkingRooftop: async rooftop => {
          const userToken = await user?.getToken();
          if (!userToken) throw new Error(`User token not found`);
          localStorage?.setItem(ROOFTOP_KEY, JSON.stringify(rooftop.id));
          setRooftop(rooftop as never);
          const organization = (rooftop as { organization?: { config?: unknown } }).organization;
          const customerConfig = await enrichCustomerConfig(userToken, rooftop.config, organization?.config);
          const organizationConfig = await enrichOrganizationConfig(userToken, organization?.config);
          if (customerConfig) setCustomerConfig(customerConfig);
          if (organizationConfig) setOrganizationConfig(organizationConfig);
        },
        restoreRooftopId,
        rememberRooftopId: id => localStorage?.setItem(ROOFTOP_KEY, JSON.stringify(id)),
      },
      products: { useRooftopFacts: rooftopId => useRooftopProductFacts(rooftopId) },
      lanes,
      transport,
      programs,
      events: { logEvent: (name, payload) => awaitableLogEvent(name, payload) },
      report: { captureException: (error, context) => Sentry.captureException(error, context as never) },
      notify: { success: notify('success'), error: notify('error'), warning: notify('warning'), info: notify('info') },
      config: { adminUrl: siteConfig.hopdrive.admin.url },
      components: { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction, useDisabledBypassTrap },
      capabilities: resolveCapabilities(),
    }),
    [transport, session, user]
  );
}
